/**
 * Password Complexity Configuration and Validation Utilities
 * 
 * This module provides centralized password complexity requirements
 * that can be used across different pages and components.
 */

// Password complexity configuration
export const PASSWORD_COMPLEXITY_CONFIG = {
  minLength: 8,
  requiredCategories: 3, // Must include at least 3 of the 4 categories
  categories: {
    uppercase: {
      regex: /[A-Z]/,
      label: 'Uppercase letter',
      description: 'At least one uppercase letter (A-Z)'
    },
    lowercase: {
      regex: /[a-z]/,
      label: 'Lowercase letter', 
      description: 'At least one lowercase letter (a-z)'
    },
    numeric: {
      regex: /[0-9]/,
      label: 'Number',
      description: 'At least one number (0-9)'
    },
    symbol: {
      regex: /[^A-Za-z0-9\s]/,
      label: 'Symbol',
      description: 'At least one special character (!@#$%^&* etc.)'
    }
  }
};

/**
 * Validates password against complexity requirements
 * @param {string} password - The password to validate
 * @returns {object} - Validation result with details
 */
export function validatePasswordComplexity(password) {
  const config = PASSWORD_COMPLEXITY_CONFIG;
  const result = {
    isValid: false,
    score: 0,
    maxScore: 4, // 1 for min length + 3 for required categories
    requirements: {
      minLength: {
        met: password.length >= config.minLength,
        required: config.minLength,
        actual: password.length,
        label: `At least ${config.minLength} characters`
      },
      categories: {}
    },
    missingRequirements: [],
    strength: 'weak'
  };

  // Check minimum length
  if (result.requirements.minLength.met) {
    result.score += 1;
  } else {
    result.missingRequirements.push(result.requirements.minLength.label);
  }

  // Check each category
  let categoriesMet = 0;
  Object.entries(config.categories).forEach(([key, category]) => {
    const met = category.regex.test(password);
    result.requirements.categories[key] = {
      met,
      label: category.label,
      description: category.description
    };
    
    if (met) {
      categoriesMet += 1;
    } else {
      result.missingRequirements.push(category.description);
    }
  });

  // Add points for categories (up to 3 points max, since we only need 3 categories)
  const categoryPoints = Math.min(categoriesMet, config.requiredCategories);
  result.score += categoryPoints;

  // Check if enough categories are met
  const categoriesRequirementMet = categoriesMet >= config.requiredCategories;
  if (!categoriesRequirementMet) {
    result.missingRequirements.push(
      `At least ${config.requiredCategories} of the 4 character types (uppercase, lowercase, number, symbol)`
    );
  }

  // Overall validation
  result.isValid = result.requirements.minLength.met && categoriesRequirementMet;

  // Calculate strength
  if (result.isValid && result.score === 4) {
    result.strength = 'strong';
  } else if (result.score >= 3) {
    result.strength = 'medium';
  } else if (result.score >= 2) {
    result.strength = 'weak';
  } else {
    result.strength = 'very-weak';
  }

  return result;
}

/**
 * Gets password strength color classes for UI
 * @param {string} strength - Password strength level
 * @returns {object} - Color classes for different UI elements
 */
export function getPasswordStrengthColors(strength) {
  const colors = {
    'very-weak': {
      bg: 'bg-red-100 dark:bg-red-900/20',
      border: 'border-red-300 dark:border-red-700',
      text: 'text-red-600 dark:text-red-400',
      progress: 'bg-red-500',
      indicator: 'bg-red-500'
    },
    'weak': {
      bg: 'bg-orange-100 dark:bg-orange-900/20',
      border: 'border-orange-300 dark:border-orange-700', 
      text: 'text-orange-600 dark:text-orange-400',
      progress: 'bg-orange-500',
      indicator: 'bg-orange-500'
    },
    'medium': {
      bg: 'bg-yellow-100 dark:bg-yellow-900/20',
      border: 'border-yellow-300 dark:border-yellow-700',
      text: 'text-yellow-600 dark:text-yellow-400', 
      progress: 'bg-yellow-500',
      indicator: 'bg-yellow-500'
    },
    'strong': {
      bg: 'bg-green-100 dark:bg-green-900/20',
      border: 'border-green-300 dark:border-green-700',
      text: 'text-green-600 dark:text-green-400',
      progress: 'bg-green-500', 
      indicator: 'bg-green-500'
    }
  };

  return colors[strength] || colors['very-weak'];
}

/**
 * Gets password strength percentage for progress bars
 * @param {number} score - Password complexity score
 * @param {number} maxScore - Maximum possible score
 * @returns {number} - Percentage (0-100)
 */
export function getPasswordStrengthPercentage(score, maxScore = 4) {
  return Math.round((score / maxScore) * 100);
}

/**
 * Legacy function for backward compatibility
 * @param {string} input - Password to validate
 * @returns {boolean} - Whether password meets basic requirements
 */
export function isValidInput(input) {
  const validation = validatePasswordComplexity(input);
  return validation.isValid;
}
